import React, { useCallback, useMemo } from "react"

import useTrackLinkContext from "decentraland-gatsby/dist/context/Track/useTrackLinkContext"
import useFormatMessage from "decentraland-gatsby/dist/hooks/useFormatMessage"

import appleLogo from "../../../images/apple-logo.svg"
import microsoftLogo from "../../../images/microsoft-logo.svg"
import decentralandLogo from "../../../images/white-dcl-logo.svg"
import { triggerFileDownload } from "../../../modules/file"
import { SectionViewedTrack, SegmentEvent } from "../../../modules/segment"
import {
  addQueryParamsToUrlString,
  updateUrlWithLastValue,
} from "../../../modules/url"
import { setUserAgentArchitectureDefautlByOs } from "../../../modules/userAgent"
import { DownloadButton } from "../../Buttons/DownloadButton"
import {
  DclWhiteLogo,
  DowloadButtonsContainer,
  DownloadActions,
  DownloadAlternativeButton,
  DownloadAlternativeButtonImage,
  DownloadAlternativeButtonsWrapper,
  DownloadAlternativeContainer,
  DownloadAlternativeTitle,
  DownloadButtonImage,
  DownloadDetail,
  DownloadSubtitle,
  DownloadTitle,
  DownloadUnavailable,
  DownloadUnavailableIFrame,
} from "./LandingDownloadOptions.styled"
import {
  DownloadOptionsProps,
  LandingDownloadOptionsProps,
  OperativeSystem,
} from "./LandingDownloadOptions.types"

const imageByOs: Record<string, string> = {
  Windows: microsoftLogo,
  macOS: appleLogo,
}

const LandingDownloadOptions = React.memo(
  (props: LandingDownloadOptionsProps) => {
    const { userAgentData, title, links, redirectPath, hideLogo } = props

    const l = useFormatMessage()

    const onClickHandle = useTrackLinkContext()

    const searchParams = new URLSearchParams(window.location.search)
    const event = searchParams.get("event")
    const os = searchParams.get("os")

    if (userAgentData && os) {
      setUserAgentArchitectureDefautlByOs(userAgentData, os as OperativeSystem)
    }

    const primaryDownloadOptions: DownloadOptionsProps[] = useMemo(() => {
      if (!userAgentData || !links[userAgentData.os.name]) {
        return []
      }

      if (
        userAgentData.os.name === OperativeSystem.MACOS &&
        links[userAgentData.os.name][userAgentData.cpu.architecture]
      ) {
        return [
          {
            text: userAgentData.os.name,
            image: imageByOs[userAgentData.os.name],
            link: links[userAgentData.os.name][userAgentData.cpu.architecture],
            arch: userAgentData.cpu.architecture,
          },
        ]
      } else if (userAgentData.os.name === OperativeSystem.MACOS) {
        return Object.keys(links[userAgentData.os.name]).map(
          (architecture) => ({
            text: userAgentData.os.name,
            archInfo: l(`page.download.${architecture}_processors`),
            image: imageByOs[userAgentData.os.name as OperativeSystem.MACOS],
            link: links[userAgentData.os.name][architecture],
            arch: architecture,
          })
        )
      }

      return [
        {
          text: userAgentData.os.name,
          image: imageByOs[userAgentData.os.name],
          link: links[userAgentData.os.name][userAgentData.cpu.architecture],
          arch: userAgentData.cpu.architecture,
        },
      ]
    }, [userAgentData, event, links])

    const secondaryDownloadOptions: DownloadOptionsProps[] = useMemo(() => {
      if (!userAgentData) {
        return []
      }

      return Object.keys(links)
        .filter((os) => os !== userAgentData.os.name)
        .flatMap((os) => {
          const isAllowedOs =
            os === OperativeSystem.MACOS || os === OperativeSystem.WINDOWS

          return {
            text: os,
            image: imageByOs[os],
            link: isAllowedOs
              ? undefined
              : links[os][Object.keys(links[os])[0]],
            redirectOs: isAllowedOs ? os : undefined,
          }
        })
    }, [userAgentData, links])

    const onClickDownloadHandler = useCallback(
      (option: DownloadOptionsProps) => {
        const params = { event }

        if (option.link) {
          const linkWithParams = addQueryParamsToUrlString(option.link, params)
          triggerFileDownload(linkWithParams)
        }

        if (option.redirectOs) {
          const redirectUrl = updateUrlWithLastValue(
            window.location.href,
            "os",
            option.redirectOs
          )
          window.location.href = addQueryParamsToUrlString(redirectUrl, params)
        } else if (redirectPath) {
          const redirectUrl = updateUrlWithLastValue(
            new URL(redirectPath, window.location.origin).toString(),
            "os",
            option.text
          )
          const finalUrl = addQueryParamsToUrlString(redirectUrl, {
            arch: option.arch,
            ...params,
          })
          setTimeout(() => {
            window.location.href = finalUrl
          }, 3000)
        }
      },
      [redirectPath, event]
    )

    return (
      <DownloadDetail>
        {!hideLogo && <DclWhiteLogo src={decentralandLogo} />}
        {primaryDownloadOptions.length === 0 && (
          <DownloadUnavailable>
            <DownloadTitle variant="h3">
              {l("page.download.not_available")}
            </DownloadTitle>
            <DownloadSubtitle>
              {l("page.download.not_available_subtitle", {
                span: (text: string) => <span>{text}</span>,
              })}
            </DownloadSubtitle>
            <DownloadUnavailableIFrame
              src="https://embeds.beehiiv.com/d7d652da-adc8-422f-9176-4b653a244020?slim=true"
              data-test-id="beehiiv-embed"
              height="52"
              frameBorder="0"
              scrolling="no"
              style={{
                margin: 0,
                borderRadius: "0px !important",
                backgroundColor: "transparent",
              }}
            />
          </DownloadUnavailable>
        )}
        {primaryDownloadOptions.length > 0 && (
          <>
            {title && <DownloadTitle variant="h3">{title}</DownloadTitle>}
            <DownloadActions>
              <DowloadButtonsContainer>
                {primaryDownloadOptions.map((option, index) => (
                  <DownloadButton
                    key={index}
                    href={option.link!}
                    onClick={(event) => {
                      event.preventDefault()
                      onClickHandle(event)

                      onClickDownloadHandler(option)
                    }}
                    event={SegmentEvent.Download}
                    place={SectionViewedTrack.Download}
                    endIcon={<DownloadButtonImage src={option.image} />}
                    label={l("page.download.download_for", {
                      os_name: option.text,
                    })}
                    subLabel={
                      option.text === OperativeSystem.MACOS
                        ? option.archInfo
                        : undefined
                    }
                    isFullWidth={false}
                  />
                ))}
              </DowloadButtonsContainer>
              <DownloadAlternativeContainer>
                <DownloadAlternativeTitle variant="body1">
                  {l("page.download.also_available")}
                </DownloadAlternativeTitle>
                <DownloadAlternativeButtonsWrapper>
                  {secondaryDownloadOptions.map((option, index) => (
                    <DownloadAlternativeButton
                      variant="text"
                      color="inherit"
                      onClick={(event) => {
                        event.preventDefault()
                        onClickHandle(event)

                        onClickDownloadHandler(option)
                      }}
                      href={option.link}
                      key={index}
                      startIcon={
                        <DownloadAlternativeButtonImage src={option.image} />
                      }
                    />
                  ))}
                </DownloadAlternativeButtonsWrapper>
              </DownloadAlternativeContainer>
            </DownloadActions>
          </>
        )}
      </DownloadDetail>
    )
  }
)

export { LandingDownloadOptions }
